import { Droplets, Flame, Sun } from "lucide-react";
import { Card } from "@/components/ui/card";
import Reveal from "@/components/layout/Reveal";
import SectionLabel from "@/components/layout/SectionLabel";

const CARDS = [
  {
    icon: Droplets,
    title: "Lower running costs",
    body: "Heat pumps can produce 3 to 5 units of hot water heat for every 1 unit of electricity used.",
    img: "https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&w=1200&q=80",
    alt: "Hot water flowing from a modern shower head",
  },
  {
    icon: Flame,
    title: "Reduce reliance on gas",
    body: "Stop paying daily gas connection charges — and move off fossil gas combustion at home.",
    img: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=1200&q=80",
    alt: "Blue gas flame on a kitchen stovetop burner",
  },
  {
    icon: Sun,
    title: "Prepare for solar & battery",
    body: "An electric hot water tank is the perfect base for future solar PV self-consumption.",
    img: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?auto=format&fit=crop&w=1200&q=80",
    alt: "Solar panels on a modern home roof under blue sky",
  },
];

export const WhyStartWithHotWaterSection = () => {
  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <Reveal className="max-w-3xl">
          <SectionLabel>Why start here</SectionLabel>
          <h2 className="font-heading mt-4 text-3xl sm:text-5xl font-bold tracking-tight text-[#0B132B]">
            Hot water is one of the biggest energy bills in a Kiwi home.
          </h2>
          <p className="mt-5 text-lg text-[#475569] leading-relaxed">
            Upgrading to a heat pump hot water system is one of the most practical first steps to lower household energy use — and prepare your home for a cleaner electric future.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <Reveal key={i} delay={i * 90}>
              <Card
                data-testid={`why-start-card-${i}`}
                className="group h-full overflow-hidden rounded-2xl border-[#E2E8F0] bg-white hover:border-[#10B981]/40 hover:shadow-xl hover:shadow-emerald-500/8 hover:-translate-y-1 transition-all duration-300"
              >
                {/* Visual */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.alt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/45 via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/95 backdrop-blur text-[#10B981] shadow-sm">
                    <c.icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                </div>
                {/* Body */}
                <div className="p-6 sm:p-7">
                  <h3 className="font-heading text-xl font-semibold text-[#0B132B] leading-tight">{c.title}</h3>
                  <p className="mt-2 text-[#475569] leading-relaxed">{c.body}</p>
                </div>
              </Card>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
